import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";
import type { Ref } from "react";
import {
  UploadImage,
  type UploadImageHandle,
} from "../../../components/form-components/image-uploader/ImageUploader";
import { ChoiceTile, FormSection } from "./partner-organization-form-ui";
import type { PartnerOrganizationFormValues } from "./partner-organization-form";

type CapabilityKey =
  | "acceptsGroups"
  | "isAccessible"
  | "acceptsEnglishSpeakers";

const CAPABILITY_OPTIONS: {
  key: CapabilityKey;
  label: string;
  description: string;
}[] = [
  {
    key: "acceptsGroups",
    label: "קולט קבוצות",
    description: "הארגון יכול לקבל קבוצות מתנדבים",
  },
  {
    key: "isAccessible",
    label: "נגישות",
    description: "המקום נגיש לאנשים עם מוגבלות",
  },
  {
    key: "acceptsEnglishSpeakers",
    label: "דוברי אנגלית",
    description: "אפשר לשבץ מתנדבים דוברי אנגלית",
  },
];

type ParentOption = { id: string; name: string };

type Props = {
  values: PartnerOrganizationFormValues;
  onChange: (next: PartnerOrganizationFormValues) => void;
  logoUploadRef: Ref<UploadImageHandle>;
  logoResetKey: string;
  parentOptions: ParentOption[];
  currentParentName?: string;
  childNames?: string[];
};

export function PartnerOrganizationProfileFields({
  values,
  onChange,
  logoUploadRef,
  logoResetKey,
  parentOptions,
  currentParentName,
  childNames = [],
}: Props) {
  const selectedParent =
    parentOptions.find((option) => option.id === values.parentId) ??
    (values.parentId && currentParentName
      ? { id: values.parentId, name: currentParentName }
      : null);
  return (
    <Stack gap={2.5}>
      <Stack direction={{ xs: "column", md: "row" }} gap={2.5} alignItems="stretch">
        <Box
          sx={{
            width: { xs: "100%", md: 220 },
            flexShrink: 0,
            p: { xs: 2, md: 3 },
            borderRadius: 2.5,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <UploadImage
            key={logoResetKey}
            ref={logoUploadRef}
            name="partnerOrgLogo"
            compact
            compactSize={128}
            imageId={values.logoImageId}
            handleImageChange={(nextLogoImageId) =>
              onChange({ ...values, logoImageId: nextLogoImageId })
            }
          />
          <Stack alignItems="center" gap={0.25}>
            <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>לוגו</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12.5, textAlign: "center" }}>
              לחצו להעלאה או החלפה
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FormSection
            title="פרטי הארגון"
            description="השם מופיע בטבלה, במפה ובאפליקציה. התיאור עוזר לצוות להבין את הארגון."
          >
            <Stack gap={2}>
              <TextField
                label="שם הארגון"
                value={values.name}
                onChange={(e) => onChange({ ...values, name: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="תיאור"
                value={values.description}
                onChange={(e) =>
                  onChange({ ...values, description: e.target.value })
                }
                fullWidth
                multiline
                minRows={4}
                maxRows={6}
              />
              <TextField
                label="הערות פנימיות"
                value={values.comments}
                onChange={(e) =>
                  onChange({ ...values, comments: e.target.value })
                }
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
              />
            </Stack>
          </FormSection>
        </Box>
      </Stack>

      <FormSection
        title="היררכיה"
        description="עד שלוש רמות. במסך הארגון רואים רק את האב ואת התתי ארגונים הישירים."
      >
        <Stack gap={1.5}>
          <Autocomplete
            options={parentOptions}
            value={selectedParent}
            onChange={(_event, option) =>
              onChange({ ...values, parentId: option?.id ?? "" })
            }
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="אין ארגון אב אפשרי"
            renderInput={(params) => (
              <TextField
                {...params}
                label="ארגון אב"
                placeholder="ללא, זה ארגון ראשי"
                helperText="מוצגים רק ארגונים שאפשר לשייך. מנהל האב יראה באפליקציה גם את המשימות של הארגון הזה."
              />
            )}
          />
          {childNames.length > 0 ? (
            <Stack gap={0.25}>
              <Typography variant="body2" fontWeight={650}>
                תתי ארגונים
              </Typography>
              {childNames.map((name, index) => (
                <Typography
                  key={`${name}-${index}`}
                  variant="body2"
                  color="text.secondary"
                >
                  {name}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </FormSection>

      <FormSection
        title="יכולות וקליטה"
        description="סמנו מה הארגון יכול לקבל. זה משפיע על שיבוץ ועל הסינון בטבלה."
      >
        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
          {CAPABILITY_OPTIONS.map((option) => (
            <ChoiceTile
              key={option.key}
              selected={values[option.key]}
              label={option.label}
              description={option.description}
              onClick={() =>
                onChange({ ...values, [option.key]: !values[option.key] })
              }
            />
          ))}
        </Stack>
      </FormSection>

      <FormSection title="פעילות שוטפת">
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <TextField
            label="מקסימום אנשים בקבוצה"
            type="number"
            value={values.maxGroupSize}
            onChange={(e) =>
              onChange({ ...values, maxGroupSize: e.target.value })
            }
            fullWidth
            inputProps={{ min: 1, step: 1 }}
          />
          <TextField
            label="שעות פעילות"
            value={values.operatingHours}
            onChange={(e) =>
              onChange({ ...values, operatingHours: e.target.value })
            }
            fullWidth
            placeholder="למשל א-ה 09:00-17:00"
          />
        </Stack>
      </FormSection>
    </Stack>
  );
}
